import type {I18nKey} from "./i18n";

export type FieldType = "text" | "email" | "select" | "file" | "number" | "password";

export type FieldRule<T extends string = string> = {
  field: T;
  type?: FieldType;
  required?: boolean;
  min?: number;
};

export function isBlank(value: unknown, type?: FieldType): boolean {
  if(value === null || value === undefined) return true;
  if(type === "select") return value === 0 || value === "" || value === "0";
  if(typeof value === "number") return false;
  if(typeof value === "string") return value.trim() === "";
  if(value instanceof FileList) return value.length === 0;
  return false;
}

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function validateField<T extends string>(
  rule: FieldRule<T>,
  value: unknown,
  t: (key: I18nKey) => string
): string | null {
  const required = rule.required !== false;
  if(required && isBlank(value, rule.type)) {
    if(rule.type === "select") return t("Please select an option");
    if(rule.type === "file") return t("Please attach a file");
    return t("This field is required");
  }
  if(rule.type === "email" && typeof value === "string" && value.trim() && !isValidEmail(value)) {
    return t("Please enter a valid e-mail address");
  }
  if(rule.type === "number" && required) {
    const n = typeof value === "number" ? value : parseFloat(String(value));
    if(isNaN(n) || (rule.min !== undefined && n < rule.min)) return t("This field is required");
  }
  return null;
}

export function validateFields<T extends string>(
  rules: FieldRule<T>[],
  values: Record<string, unknown>,
  t: (key: I18nKey) => string
): Partial<Record<T, string>> {
  const errors: Partial<Record<T, string>> = {};
  for(const rule of rules) {
    const err = validateField(rule, values[rule.field], t);
    if(err) errors[rule.field] = err;
  }
  return errors;
}
